import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    ClipboardList,
    CheckSquare,
    Calendar,
    BookOpen,
    CalendarDays,
    Users,
    FileText
} from 'lucide-react';

import ModuleLayout from '../../layouts/ModuleLayout';
import Drawer from '../../components/common/Drawer';
import PermissionGate from '../../components/common/PermissionGate/PermissionGate';

// Import existing components
import {
    AttendanceDashboard,
    AttendanceLogs,
    AttendanceRegularization,
    LeaveRequests,
    LeavePolicies,
    HolidayCalendar,
    ShiftRoster
} from './attendance-leaves';

// Access Denied Component
const AccessDenied = () => (
    <div className="flex flex-col items-center justify-center p-12 text-center text-gray-500 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p>You do not have permission to view this section.</p>
    </div>
);

const AttendanceLeaveModule = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Define Tabs
    const tabs = [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'attendanceLogs', label: 'Attendance Logs', icon: ClipboardList },
        { id: 'regularization', label: 'Regularization Requests', icon: CheckSquare },
        { id: 'leaveRequests', label: 'Leave Requests', icon: Calendar },
        { id: 'shiftRoster', label: 'Shift & Roster', icon: Users },
        { id: 'holidayCalendar', label: 'Holiday Calendar', icon: CalendarDays },
        { id: 'leavePolicies', label: 'Leave Policies', icon: BookOpen },
    ];

    // Determine active tab from URL or Path
    const getTabFromPath = () => {
        const path = location.pathname;
        if (path.includes('/attendance_dashboard')) return 'dashboard';
        if (path.includes('/attendance_logs')) return 'attendanceLogs';
        if (path.includes('/regularization')) return 'regularization';
        if (path.includes('/leave_requests')) return 'leaveRequests';
        if (path.includes('/leave_policies')) return 'leavePolicies';
        if (path.includes('/holiday_calendar')) return 'holidayCalendar';
        if (path.includes('/shift_roster')) return 'shiftRoster';
        return 'dashboard';
    };

    const initialTab = searchParams.get('tab') || getTabFromPath();
    const [activeTab, setActiveTab] = useState(initialTab);

    // Sync tab state with URL
    useEffect(() => {
        const currentTabParam = searchParams.get('tab');
        if (currentTabParam && currentTabParam !== activeTab) {
            setActiveTab(currentTabParam);
        } else if (!currentTabParam) {
            const pathTab = getTabFromPath();
            if (pathTab !== activeTab) {
                setActiveTab(pathTab);
            }
        }
    }, [searchParams, location.pathname]);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setSearchParams({ tab: tabId });
    };

    // Drawer State
    const action = searchParams.get('action');
    const actionId = searchParams.get('id');

    const closeDrawer = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('action');
        newParams.delete('id');
        setSearchParams(newParams);
    };

    // Render Tab Content
    const renderTabContent = () => {
        switch (activeTab) {
            case 'dashboard':
                return (
                    <PermissionGate permission="attendance" action="read" showFallback fallback={<AccessDenied />}>
                        <AttendanceDashboard />
                    </PermissionGate>
                );
            case 'attendanceLogs':
                return (
                    <PermissionGate permission="attendance" action="read" showFallback fallback={<AccessDenied />}>
                        <AttendanceLogs />
                    </PermissionGate>
                );
            case 'regularization':
                return (
                    <PermissionGate permission="attendance" action="read" showFallback fallback={<AccessDenied />}>
                        <AttendanceRegularization />
                    </PermissionGate>
                );
            case 'leaveRequests':
                return (
                    <PermissionGate permission="attendance" action="read" showFallback fallback={<AccessDenied />}>
                        <LeaveRequests />
                    </PermissionGate>
                );
            case 'leavePolicies':
                return (
                    <PermissionGate permission="attendance" action="read" showFallback fallback={<AccessDenied />}>
                        <LeavePolicies />
                    </PermissionGate>
                );
            case 'holidayCalendar':
                return (
                    <PermissionGate permission="attendance" action="read" showFallback fallback={<AccessDenied />}>
                        <HolidayCalendar />
                    </PermissionGate>
                );
            case 'shiftRoster':
                return (
                    <PermissionGate permission="attendance" action="read" showFallback fallback={<AccessDenied />}>
                        <ShiftRoster />
                    </PermissionGate>
                );
            default:
                return <AttendanceDashboard />;
        }
    };

    // Render Drawer Content Helper
    const renderDrawerContent = () => {
        if (!action) return null;

        // Fallback for missing components
        const FallbackComponent = ({ title }) => (
            <div className="p-6 text-center">
                <div className="mb-4 text-gray-400">
                    <FileText className="w-12 h-12 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">{title}</h3>
                <p className="text-gray-500 mt-2">
                    This form is being integrated into the new drawer system.
                    Please use the existing list view actions for now.
                </p>
                <button
                    onClick={closeDrawer}
                    className="mt-6 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                    Close
                </button>
            </div>
        );

        switch (action) {
            case 'createRegularization':
                return <AttendanceRegularization initialMode="create" onClose={closeDrawer} />;
            case 'viewRegularization':
                return <AttendanceRegularization initialMode="view" id={actionId} onClose={closeDrawer} />;
            case 'applyLeave':
                return <LeaveRequests initialMode="create" onClose={closeDrawer} />;
            case 'viewLeave':
                return <LeaveRequests initialMode="view" id={actionId} onClose={closeDrawer} />;
            case 'createLeavePolicy':
                return <LeavePolicies initialMode="create" onClose={closeDrawer} />;
            case 'viewLeavePolicy':
                return <LeavePolicies initialMode="view" id={actionId} onClose={closeDrawer} />;
            case 'createHoliday':
                return <HolidayCalendar initialMode="create" onClose={closeDrawer} />;
            case 'viewHoliday':
                return <HolidayCalendar initialMode="view" id={actionId} onClose={closeDrawer} />;
            case 'createShift':
                return <ShiftRoster initialMode="create_shift" onClose={closeDrawer} />;
            case 'editShift':
                return <ShiftRoster initialMode="edit_shift" id={actionId} onClose={closeDrawer} />;
            case 'viewShift':
                return <ShiftRoster initialMode="view_shift" id={actionId} onClose={closeDrawer} />;
            case 'assignRoster':
                return <ShiftRoster initialMode="assign_roster" id={actionId} onClose={closeDrawer} />;
            case 'editRoster':
                return <ShiftRoster initialMode="edit_roster" id={actionId} onClose={closeDrawer} />;
            case 'viewRoster':
                return <ShiftRoster initialMode="view_roster" id={actionId} onClose={closeDrawer} />;
            default:
                return null;
        }
    };

    const getDrawerTitle = () => {
        switch (action) {
            case 'createRegularization': return 'Create Regularization';
            case 'viewRegularization': return 'Regularization Details';
            case 'applyLeave': return 'Apply Leave';
            case 'viewLeave': return 'Leave Request Details';
            case 'createLeavePolicy': return 'Create Leave Policy';
            case 'viewLeavePolicy': return 'Leave Policy Details';
            case 'createHoliday': return 'Create Holiday';
            case 'viewHoliday': return 'Holiday Details';
            case 'createShift': return 'Create Shift';
            case 'editShift': return 'Edit Shift';
            case 'viewShift': return 'Shift Details';
            case 'assignRoster': return 'Assign Roster';
            case 'editRoster': return 'Edit Roster Assignment';
            case 'viewRoster': return 'Roster Assignment Details';
            default: return 'Details';
        }
    };

    return (
        <ModuleLayout
            title="Attendance & Leave"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
        >
            {renderTabContent()}

            <Drawer
                isOpen={!!action}
                onClose={closeDrawer}
                title={getDrawerTitle()}
                size="large"
            >
                {renderDrawerContent()}
            </Drawer>
        </ModuleLayout>
    );
};

export default AttendanceLeaveModule;
